import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import { identifyUser, track } from '../utils/analytics';
const API = API_BASE;

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

const PasswordInput = React.forwardRef(function PasswordInput({ value, onChange, placeholder, autoComplete, style }, ref) {
  const [visible, setVisible] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={ref}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        required
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={{ ...style, paddingRight: '2.5rem' }}
      />
      <button
        type="button"
        onClick={() => setVisible(v => !v)}
        style={{
          position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', color: '#aaa',
          display: 'flex', alignItems: 'center', padding: 0, width: 'auto',
        }}
        tabIndex={-1}
      >
        <EyeIcon open={visible} />
      </button>
    </div>
  );
});

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // DOM refs as a fallback for Chrome's autofill quirk: when the browser
  // pre-fills the inputs it doesn't always fire onChange, so React state
  // stays empty until the user clicks something. On submit we read the
  // actual DOM values via these refs if state is still empty.
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // Forgot password state
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // 2FA state — entered when the login response signals requires2FA.
  const [twoFAStep, setTwoFAStep] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    // Fall back to DOM values when React state hasn't caught up with
    // Chrome's autofill (state can be '' even though the input has a value).
    const finalEmail = (email || emailRef.current?.value || '').trim();
    const finalPassword = password || passwordRef.current?.value || '';
    if (!finalEmail || !finalPassword) {
      setError('Email and password are required.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email: finalEmail, password: finalPassword });
      if (res.data?.requires2FA && res.data?.tempToken) {
        setTempToken(res.data.tempToken);
        setTwoFACode('');
        setTwoFAStep(true);
        setLoading(false);
        return;
      }
      localStorage.setItem('guruone_token', res.data.token);
      localStorage.setItem('guruone_user', JSON.stringify(res.data.user));
      navigate('/dashboard');
      track('user_logged_in');
      identifyUser(res.data.user.id, res.data.user.email, res.data.user.businessName);
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    }
    setLoading(false);
  }

  async function handleTwoFA(e) {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/2fa/authenticate`,
        { token: twoFACode },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      localStorage.setItem('guruone_token', res.data.token);
      localStorage.setItem('guruone_user', JSON.stringify(res.data.user));
      navigate('/dashboard');
      track('user_logged_in', { method: '2fa' });
      identifyUser(res.data.user.id, res.data.user.email, res.data.user.businessName);
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed. Please try again.');
    }
    setLoading(false);
  }

  function backToLogin() {
    setTwoFAStep(false);
    setTwoFACode('');
    setTempToken('');
    setError('');
  }

  async function handleForgot(e) {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: resetEmail.trim() });
      setResetSent(true);
    } catch {
      setResetError('Something went wrong. Please try again.');
    }
    setResetLoading(false);
  }

  const inputStyle = {
    width: '100%', padding: '0.75rem 0.9rem', borderRadius: 8,
    border: '1.5px solid #e5e7eb', fontSize: '0.9rem', fontFamily: 'inherit',
    boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.15s',
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#f7f7f9',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem',
    }}>
      {/* Brand */}
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <img src="/Logo.png" alt="GuruOne" style={{ height: 88, display: 'block', margin: '0 auto 0.75rem' }} />
        <div style={{ fontSize: '0.82rem', color: '#aaa' }}>Your virtual C-suite</div>
      </div>

      {/* ── 2FA CARD ── */}
      {twoFAStep && (
        <div style={{ background: 'white', borderRadius: 16, padding: '2.5rem', width: '100%', maxWidth: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', color: '#1a1a2e', fontWeight: 700 }}>Two-Factor Authentication</h2>
          <p style={{ fontSize: '0.82rem', color: '#888', margin: '0 0 1.5rem', lineHeight: 1.55 }}>
            Enter the 6-digit code from your authenticator app.
          </p>

          <form onSubmit={handleTwoFA} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>Verification code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={twoFACode}
                onChange={e => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoFocus
                placeholder="123456"
                autoComplete="one-time-code"
                style={{ ...inputStyle, letterSpacing: '0.4em', textAlign: 'center', fontSize: '1.05rem', fontVariantNumeric: 'tabular-nums' }}
              />
            </div>

            {error && (
              <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#991B1B' }}>
                {error}
                {/invalid code/i.test(error) && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#7c2d2d', lineHeight: 1.55 }}>
                    <strong>Have you re-enabled 2FA recently?</strong> If you disabled and then re-enabled 2FA on this account, your authenticator app may have two GuruOne entries. Make sure you are using a code from the most recently scanned entry — codes from the old one will not work. You can also use one of your backup codes.
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || twoFACode.length !== 6}
              style={{
                padding: '0.85rem', borderRadius: 8, background: '#1D9E75', color: 'white',
                border: 'none', fontSize: '0.95rem', fontWeight: 600, width: '100%',
                cursor: loading || twoFACode.length !== 6 ? 'not-allowed' : 'pointer',
                opacity: loading || twoFACode.length !== 6 ? 0.6 : 1,
                fontFamily: 'inherit', marginTop: '0.5rem', transition: 'opacity 0.15s',
              }}>
              {loading ? 'Verifying...' : 'Verify →'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <button
              onClick={backToLogin}
              style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline', padding: 0 }}>
              Back to login
            </button>
          </div>
        </div>
      )}

      {/* ── LOGIN CARD ── */}
      {!twoFAStep && !showReset && (
        <div style={{ background: 'white', borderRadius: 16, padding: '2.5rem', width: '100%', maxWidth: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <h2 style={{ margin: '0 0 1.75rem', fontSize: '1.25rem', color: '#1a1a2e', fontWeight: 700 }}>Welcome back</h2>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>Email</label>
              <input
                ref={emailRef}
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                required placeholder="you@example.com" autoComplete="email" style={inputStyle}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>Password</label>
              <PasswordInput
                ref={passwordRef}
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••••" autoComplete="current-password" style={inputStyle}
              />
            </div>

            {error && (
              <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#991B1B' }}>
                {error}
              </div>
            )}

            <button
              type="submit" disabled={loading}
              style={{
                padding: '0.85rem', borderRadius: 8, background: '#1D9E75', color: 'white',
                border: 'none', fontSize: '0.95rem', fontWeight: 600, width: '100%',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                fontFamily: 'inherit', marginTop: '0.5rem', transition: 'opacity 0.15s',
              }}>
              {loading ? 'Logging in...' : 'Log in →'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
            <button
              onClick={() => { setShowReset(true); setResetEmail(email); setError(''); }}
              style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline', padding: 0 }}>
              Forgot password?
            </button>
          </div>

          <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.82rem', color: '#888' }}>
            Don't have an account?{' '}
            <Link to="/signup" style={{ color: '#1D9E75', fontWeight: 600, textDecoration: 'none' }}>Sign up free</Link>
          </div>
        </div>
      )}

      {/* ── FORGOT PASSWORD CARD ── */}
      {!twoFAStep && showReset && (
        <div style={{ background: 'white', borderRadius: 16, padding: '2.5rem', width: '100%', maxWidth: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <button
            onClick={() => { setShowReset(false); setResetSent(false); setResetError(''); }}
            style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit', padding: 0, marginBottom: '1.25rem' }}>
            ← Back to login
          </button>

          <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', color: '#1a1a2e', fontWeight: 700 }}>Forgot your password?</h2>

          {!resetSent ? (
            <>
              <p style={{ fontSize: '0.82rem', color: '#888', margin: '0 0 1.5rem', lineHeight: 1.55 }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
              <form onSubmit={handleForgot} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '0.4rem' }}>Email</label>
                  <input
                    type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                    required placeholder="you@example.com" autoComplete="email"
                    style={inputStyle}
                  />
                </div>

                {resetError && (
                  <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#991B1B' }}>
                    {resetError}
                  </div>
                )}

                <button
                  type="submit" disabled={resetLoading || !resetEmail}
                  style={{
                    padding: '0.85rem', borderRadius: 8, background: '#1D9E75', color: 'white',
                    border: 'none', fontSize: '0.9rem', fontWeight: 600, width: '100%',
                    cursor: resetLoading || !resetEmail ? 'not-allowed' : 'pointer',
                    opacity: resetLoading || !resetEmail ? 0.6 : 1,
                    fontFamily: 'inherit', transition: 'opacity 0.15s',
                  }}>
                  {resetLoading ? 'Sending...' : 'Send reset link →'}
                </button>
              </form>
            </>
          ) : (
            <div style={{ background: '#f0fdf8', border: '1px solid #a7f3d0', borderRadius: 10, padding: '1.5rem', textAlign: 'center', marginTop: '0.5rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📬</div>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#065f46', marginBottom: '0.5rem' }}>Check your inbox</div>
              <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 1.25rem', lineHeight: 1.6 }}>
                If an account exists for <strong>{resetEmail}</strong>, a password reset link has been sent. The link expires in 1 hour.
              </p>
              <p style={{ fontSize: '0.75rem', color: '#aaa', margin: 0 }}>
                Didn't receive it? Check your spam folder or{' '}
                <button onClick={() => setResetSent(false)} style={{ background: 'none', border: 'none', color: '#1D9E75', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'inherit', padding: 0, textDecoration: 'underline' }}>
                  try again
                </button>.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
